import { useEffect, useState } from "react";

export interface NowPlayingInfo {
  program?: string;
  song?: string;
}

/**
 * Poll the server's Sveriges Radio "now playing" endpoint for the given channel
 * ids (the stations' `metadataId`s), refreshing ~45s. Returns a map id ->
 * { program?, song? }. Stations without a metadataId are simply absent from the
 * map, so the caller falls back to the static `nowPlaying` copy.
 */
export function useRadioNowPlaying(
  ids: Array<string | undefined>
): Record<string, NowPlayingInfo> {
  const [data, setData] = useState<Record<string, NowPlayingInfo>>({});
  // Stable dependency key so the effect only re-binds when the id set changes.
  const key = [...new Set(ids.filter((x): x is string => !!x))]
    .sort()
    .join(",");
  useEffect(() => {
    if (!key) {
      setData({});
      return;
    }
    let alive = true;
    async function poll() {
      try {
        const res = await fetch(
          `/api/frontlayer/v1/radio/now-playing?ids=${encodeURIComponent(key)}`
        );
        if (!res.ok) return;
        const json = (await res.json()) as Record<string, NowPlayingInfo>;
        if (alive) setData(json);
      } catch {
        // Keep the last good value on a network blip.
      }
    }
    void poll();
    const t = window.setInterval(poll, 45_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [key]);
  return data;
}
